// i18n
import { translateInComponent, translate } from 'infras/i18n';
import translations from './i18n.json';
import {
  ReactNode,
  KeyboardEvent,
  useEffect,
  useState,
  SyntheticEvent,
} from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useRouter } from 'next/router';
import EventContentPaymentDetail from 'presentations/views/components/molecules/EventContentPaymentDetail';
import Pagination from 'presentations/views/components/atoms/Pagination';
import FloatSettingMenu from 'presentations/views/components/molecules/FloatSettingMenu';
import ConfirmationDialog from 'presentations/views/components/molecules/ConfirmationDialog';
import UserEventTagList from 'presentations/views/components/molecules/UserEventTagList';
import Accordion from 'presentations/views/components/atoms/Accordion';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import Avatar from 'presentations/views/components/atoms/Avatar';
import Link from 'presentations/views/components/atoms/Link';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import Image from 'presentations/views/components/atoms/Image';
import RichReadView from 'presentations/views/components/atoms/RichReadView';
import Tabs from 'presentations/views/components/atoms/Tabs';
import Tooltip from 'presentations/views/components/atoms/Tooltip';
import EventMetaTagList from 'presentations/views/components/molecules/EventMetaTagList';
import CalendarColor from 'presentations/views/components/atoms/CalendarColor';
import ActivityTimelineList from 'presentations/views/components/organisms/ActivityTimelineList';
import Progress from 'presentations/views/components/atoms/Progress';
import MeetingButton from 'presentations/views/components/molecules/MeetingButton';

import { ViewerItem } from 'presentations/api/schema/user';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  mediaMaxWithLayoutTheme,
  spacing,
} from 'presentations/views/styles';
import { formatToDateTime, formatToTime, formatToYMD } from 'infras/datetime';
import { isUrl } from 'infras/validator';
import {
  black,
  blueGrayPale,
  gray01,
  gray02,
  gray04,
  gray05,
  gray06,
  paper,
  primary,
  white,
} from 'presentations/views/styles/color';
import { formatDateTimeRange } from 'presentations/views/helpers/formatter';
import { LocationType } from 'domains/models/event_type/locationType';
import {
  findVideoServiceTypeFromText,
  findVideoServiceTypeFromUrl,
} from 'domains/models/calendar/videoServiceType';
import {
  isInquirySourceType,
  SourceType,
} from 'domains/models/event_source/sourceType';
import { EventDetailItem } from 'presentations/api/schema/event';
import { Status } from 'domains/models/event/status';
import { ProjectListItem } from 'presentations/api/schema/project';

import { useEventContent } from './hooks';
import { Role } from 'domains/models/event_type_user/role';
import { moveFromOrDefaultPath } from 'presentations/views/helpers/router';
import {
  dashboardPath,
  dashboardHostEventPath,
  eventTypePath,
} from 'presentations/routes/path';
import {
  ProviderType,
  providerTypeIconPath,
  providerTypeName,
} from 'domains/models/calendar/providerType';
import { DisplayType } from 'domains/models/event_type/displayType';
import { VisibilityType } from 'domains/models/event/visibilityType';
import { AnswerListItem } from 'presentations/api/schema/answer';
import { UserType } from 'domains/models/event_type_user/user_type';
import { Role as TeamUserRole } from 'domains/models/team_user/role';
import { PaymentMethod } from 'domains/models/event_payment/payment_method';
import { ReservationMethod } from 'domains/models/event_type/reservationMethod';
import { autolink } from 'domains/models/event/description';
import { Behavior } from 'domains/models/event/behavior';
import { isInElectron } from 'presentations/views/helpers/electron';
import DashboardContentItemTemplate, {
  LoadingComponentTemplate,
} from 'presentations/views/components/templates/DashboardContentItemTemplate';
import { toContentText } from 'presentations/views/helpers/answer';
import { UserAppItem } from 'presentations/api/schema/account';
import { IntegrationType } from 'domains/models/account/providerType';
import ConfirmRequestButton from './components/ConfirmRequestButton';
import Snackbar, {
  SeverityType,
} from 'presentations/views/components/atoms/Snackbar';
import ContactEditForm from '../ContactEditForm';
import { AttendType } from 'domains/models/event_type/attendType';

interface Props {
  isLoading?: boolean;
  event?: EventDetailItem;
  uid: string;
  eventUid?: string | number;
  sourceType: SourceType | null;
  pageableId: string | null;
  pageableType: string;
  providerType?: ProviderType;
  displayType?: DisplayType;
  visibilityType?: VisibilityType;
  name: string;
  description?: string | null;
  viewer: ViewerItem;
  messageList?: ReactNode;
  userList?: ReactNode;
  answerList?: ReactNode;
  startedAt?: string | null;
  endedAt: string | null;
  isAllDay?: boolean;
  timeZone?: string | null;
  routineScheduleString?: string;
  locationId?: LocationType | null;
  url?: string | null;
  address?: string | null;
  access?: string | null;
  phoneNumber?: string | null;
  isHost: boolean | null;
  customLocation?: string | null;
  status: Status;
  project?: ProjectListItem | null;
  eventTypeUserName?: string | null;
  eventTypeTeamName?: string | null;
  guestInfo?: {
    companyName?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    textRemindCountryCode?: string | null;
    textRemindNumber?: string | null;
    answers?: AnswerListItem[];
  };
  isEditDisable?: boolean;
  canShowDetail?: boolean;
  isViewerEvent: boolean;
  userApp?: UserAppItem | null;
  onClickCreatePage?: (integrationId: IntegrationType) => void;
  isCreatingPage?: boolean;
  onUpdateAnswer?: any;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state, actions } = useEventContent({
    viewer: props.viewer,
    event: props.event,
    eventUid: props.eventUid,
    sourceType: props.sourceType,
  });

  const router = useRouter();

  const videoServices = [];
  if (props.url) {
    const videoServiceType = findVideoServiceTypeFromUrl(props.url);
    if (videoServiceType) {
      videoServices.push({
        url: props.url,
        videoServiceType,
      });
    }
  }
  if (props.description) {
    const matches = findVideoServiceTypeFromText(props.description);
    if (matches) {
      videoServices.push({
        url: matches.url,
        videoServiceType: matches.videoServiceType,
      });
    }
  }

  const isPoll = props.event?.reservationMethod === ReservationMethod.Poll;
  const guestListTabs = [];
  guestListTabs.push({
    label: (
      <GuestListTabContainer>
        <GuestListTabText>{isPoll ? t('Voting') : t('Going')}</GuestListTabText>
        {state.childActiveEventsCount && (
          <GuestListTabCount>
            <GuestListTabCountInnter>
              {state.childActiveEventsCount}
            </GuestListTabCountInnter>
          </GuestListTabCount>
        )}
      </GuestListTabContainer>
    ),
  });
  if (!isPoll) {
    guestListTabs.push({
      label: (
        <GuestListTabContainer>
          <GuestListTabText>{t('Cancelled')}</GuestListTabText>
          {state.childCancelEventsCount && (
            <GuestListTabCount>
              <GuestListTabCountInnter>
                {state.childCancelEventsCount}
              </GuestListTabCountInnter>
            </GuestListTabCount>
          )}
        </GuestListTabContainer>
      ),
    });
  }

  const settingMenuOptions = [];
  if (props.isHost && !props.event?.isSeminar) {
    settingMenuOptions.push({
      icon: 'edit',
      label: t('Edit'),
      disabled: props.isEditDisable,
    });
  }
  if (
    props.event?.sourceType === SourceType.EventType &&
    (!props.event?.isSeminar || !props.event.isParent)
  ) {
    settingMenuOptions.push({
      icon: 'update',
      label: t('Reschedule'),
      disabled: props.isEditDisable,
    });
  }
  if (props.sourceType !== SourceType.IntegrationCalendar) {
    settingMenuOptions.push({
      icon: 'delete',
      label:
        props.sourceType === SourceType.EventType ? t('Cancel') : t('Delete'),
    });
  }

  let isEditable = false;
  if (
    props.event &&
    props.status === Status.Open &&
    !isInquirySourceType(props.sourceType)
  ) {
    for (const user of props.event.users) {
      if (props.viewer.uid === user.uid && user.role <= Role.Editor) {
        isEditable = true;
      }
    }
    if (props.viewer.role <= TeamUserRole.Admin) {
      isEditable = true;
    }
  }

  const headerTitle = () => {
    if (props.sourceType) {
      if (props.sourceType === SourceType.EventTypeInquiry) {
        return t('Inquiry');
      } else {
        if (props.event?.behavior === Behavior.Work) {
          return t('Task');
        }
        return t('Event');
      }
    } else {
      return t('Task');
    }
  };

  const childEvents = state.childEvents || [];

  const defaultTimeZone =
    props.sourceType === SourceType.EventType &&
    props.isHost &&
    props.event?.defaultTimeZone !== null
      ? props.event?.defaultTimeZone
      : props.timeZone;

  let cancelDialogText = '';
  if (props.isHost && props.event) {
    const event = props.event;
    if (event.isSeminar) {
      cancelDialogText += t(
        `Seminar ${event.isParent ? 'All' : 'Guest'} Cancel Notice`,
      );
    }
    if (event.payment?.paymentMethod === PaymentMethod.Card) {
      if (cancelDialogText.length > 0) {
        cancelDialogText += '\n\n';
      }
      cancelDialogText += t('Refund Notice');
    }
  }

  const isShowPrice = props.event?.payment?.items.some(
    (item) => item.isShowPrice,
  );

  const onUpdateAnswer = () => {
    if (props.onUpdateAnswer) {
      props.onUpdateAnswer();
    }
    actions.onCloseDialog();
  };

  return (
    <>
      <DashboardContentItemTemplate
        uid={props.uid}
        viewer={props.viewer}
        eventId={props.event?.id || undefined}
        liveUser={state.liveUser}
        pageableType={props.pageableType}
        pageableId={props.pageableId}
        sourceType={props.sourceType}
        messageList={props.messageList}
        canShowDetail={props.canShowDetail}
        mainContentTab={headerTitle()}
        isHost={props.isHost}
        header={
          <>
            <HeaderTitle isElectron={state.isElectron}>
              <HeaderTitleText>{headerTitle()}</HeaderTitleText>
              <UserEventTagList
                isHost={
                  props.sourceType === SourceType.EventType
                    ? props.event?.attendType === AttendType.Agent ||
                      props.isHost
                    : null
                }
                status={props.status}
                displayStatus={
                  props.sourceType !== SourceType.EventType
                    ? {
                        displayType: props.displayType,
                        visibilityType: props.visibilityType,
                      }
                    : undefined
                }
                flexDirection="row"
              />
            </HeaderTitle>
            <HeaderButtonContainer>
              {isEditable && (
                <Tooltip title={t('Edit')}>
                  <SettingButtonWrap onClick={actions.onClickSettingMenu}>
                    <SettingIcon name="dots-horizontal" />
                  </SettingButtonWrap>
                </Tooltip>
              )}
              <Tooltip title={t('Close')}>
                <CloseButtonWrap onClick={actions.onClickBack}>
                  <CloseIcon name="close" />
                </CloseButtonWrap>
              </Tooltip>
            </HeaderButtonContainer>
            <FloatSettingMenu
              onSelectMenu={actions.onSelectSettingMenu}
              onCloseMenu={actions.onCloseSettingMenu}
              anchor={state.settingMenuAnchor}
              options={settingMenuOptions}
            />
          </>
        }
        activityList={
          <ActivityList>
            {props.event &&
              state.activities.map((activity) => {
                return (
                  <ActivityTimelineList
                    event={props.event!}
                    activity={activity}
                  />
                );
              })}
          </ActivityList>
        }
        mainContent={
          <>
            <EventContentHeader>
              {props.status === Status.Request && (
                <StyledConfirmRequestButtonWrapper>
                  <ConfirmRequestButton
                    viewer={props.viewer}
                    eventUid={props.event?.uid}
                    startedAt={props.startedAt}
                    endedAt={props.endedAt}
                    isRequestAcceptLoading={state.isRequestAcceptLoading}
                    isRequestAcceptDisabled={state.isRequestAcceptDisabled}
                    isRequestDeclineLoading={state.isRequestDeclineLoading}
                    onClickAcceptRequest={actions.onClickAcceptRequest}
                    onClickDeclineRequest={actions.onClickDeclineRequest}
                  />
                </StyledConfirmRequestButtonWrapper>
              )}
              {props.sourceType === SourceType.EventType ? (
                <>
                  <EventTypeTitle>{props.name}</EventTypeTitle>
                  <UserContainer>
                    <UserName>{props.eventTypeUserName}</UserName>
                    {props.eventTypeTeamName && (
                      <CompanyName>{props.eventTypeTeamName}</CompanyName>
                    )}
                  </UserContainer>
                </>
              ) : (
                <EventTitle>{props.name}</EventTitle>
              )}
              {props.startedAt &&
                props.endedAt &&
                defaultTimeZone &&
                props.sourceType !== SourceType.EventTypeInquiry && (
                  <EventDateContainer>
                    <EventDateBox>
                      <EventDate>
                        {formatDateTimeRange(
                          props.startedAt,
                          props.endedAt,
                          defaultTimeZone,
                          props.isAllDay,
                        )}
                        &nbsp;({defaultTimeZone})
                      </EventDate>
                      {props.timeZone !== defaultTimeZone && (
                        <GuestEventDate>
                          <GuestEventDateTitle>
                            {t('Guest Date')}
                          </GuestEventDateTitle>
                          {formatDateTimeRange(
                            props.startedAt,
                            props.endedAt,
                            props.timeZone || defaultTimeZone,
                            props.isAllDay,
                          )}
                          &nbsp;({props.timeZone})
                        </GuestEventDate>
                      )}
                    </EventDateBox>
                    {props.routineScheduleString && (
                      <EventRoutineSchedule>
                        {props.routineScheduleString}
                      </EventRoutineSchedule>
                    )}
                  </EventDateContainer>
                )}
              {!props.startedAt && props.endedAt && (
                <EventDateContainer>
                  <EventDate>
                    <EventDateIcon
                      name="clock-time-nine-outline"
                      size={fontSize(18)}
                    />
                    {formatToDateTime(props.endedAt)}
                  </EventDate>
                </EventDateContainer>
              )}

              {props.event?.eventType && props.event.team && (
                <Calendar>
                  <CalendarColor color={props.event.eventType.color} />
                  <CalendarLink
                    target="_blank"
                    href={eventTypePath({
                      teamSlug: props.event.team.slug,
                      eventTypeSlug: props.event.eventType.slug,
                    })}
                  >
                    {props.event.eventType.name}
                  </CalendarLink>
                </Calendar>
              )}
              {props.event?.calendar && (
                <CalendarContainer>
                  <Calendar>
                    {props.event.calendar.color && (
                      <CalendarColor color={props.event.calendar.color} />
                    )}
                    <CalendarName>{props.event.calendar.name}</CalendarName>
                  </Calendar>
                  {props.canShowDetail &&
                    props.isViewerEvent &&
                    props.providerType !== undefined &&
                    props.providerType !== ProviderType.Jicoo && (
                      <EventNameIcon
                        src={providerTypeIconPath(props.providerType)}
                        alt={providerTypeName(props.providerType)}
                        width={20}
                        height={20}
                      />
                    )}
                </CalendarContainer>
              )}

              {props.event && (
                <MetaTagList>
                  <EventMetaTagList
                    event={props.event}
                    projectName={props.project?.name}
                    projectIcon={props.project?.icon}
                  />
                </MetaTagList>
              )}
              {props.event?.status === Status.Open && (
                <VideoList>
                  {videoServices.map((videoService) => (
                    <StyledMeetingButton
                      videoServiceType={videoService.videoServiceType}
                      url={videoService.url}
                      isShowTitleServiceName
                    />
                  ))}
                </VideoList>
              )}
              <EventSummary>
                {props.userList && (
                  <StyledSummaryContent>
                    <StyledIcon name="account" size={fontSize(18)} />
                    <StyledIconContent>{props.userList}</StyledIconContent>
                  </StyledSummaryContent>
                )}
                {props.event?.userResourceSets &&
                  props.event.userResourceSets.length > 0 && (
                    <StyledSummaryContent>
                      <StyledIcon name="view-grid-plus" size={fontSize(18)} />
                      <StyledIconContent>
                        {props.event?.userResourceSets
                          ?.map((e) => e.title)
                          .join('/')}
                      </StyledIconContent>
                    </StyledSummaryContent>
                  )}
                {props.event?.page ? (
                  <StyledSummaryContent>
                    <LocationLink href={props.event.page.url} target="_blank">
                      <LocationLinkIcon
                        name="file-document-outline"
                        size={fontSize(20)}
                      />
                      {translate(
                        `IntegrationType_${props.event.page.integrationId}`,
                      )}
                    </LocationLink>
                  </StyledSummaryContent>
                ) : props.sourceType ===
                  SourceType.IntegrationCalendar ? null : (
                  props.userApp?.isNotion &&
                  !props.event?.routine && (
                    <StyledSummaryContent>
                      <CreatePageButton
                        onClick={(e: any) =>
                          props.onClickCreatePage
                            ? props.onClickCreatePage(IntegrationType.Notion)
                            : null
                        }
                      >
                        <LocationLinkIcon
                          name="note-text-outline"
                          size={fontSize(20)}
                        />
                        {props.isCreatingPage ? (
                          <Progress size={12} />
                        ) : (
                          t('Create a Notion Page')
                        )}
                      </CreatePageButton>
                    </StyledSummaryContent>
                  )
                )}
              </EventSummary>

              {props.event?.capacity && (
                <>
                  {isPoll ? (
                    <EventCapacity>
                      <Icon name="account-group" size={fontSize(16)} />
                      <EventCapacityDetail>
                        {t('Voter', {
                          limit:
                            props.event.capacity.active +
                            props.event.capacity.cancel,
                        })}
                      </EventCapacityDetail>
                    </EventCapacity>
                  ) : (
                    <EventCapacity>
                      <Icon
                        name="ticket-confirmation-outline"
                        size={fontSize(16)}
                      />
                      <EventCapacityDetail>
                        {t('Capacity', {
                          active: props.event.capacity.active,
                          limit: props.event.capacity.limit,
                          cancel: props.event.capacity.cancel,
                        })}
                      </EventCapacityDetail>
                    </EventCapacity>
                  )}
                </>
              )}

              {(props.address ||
                props.access ||
                props.phoneNumber ||
                props.customLocation) && (
                <EventLocation>
                  <EventLocationIcon name="map-marker" size={fontSize(18)} />
                  <span>
                    {props.address ? (
                      <a
                        href={`https://www.google.com/maps/place/${props.address}`}
                        target="_blank"
                      >
                        {props.address}
                      </a>
                    ) : null}
                    {props.address && props.access && ', '}
                    {props.access}
                    {props.phoneNumber}
                  </span>
                  {isUrl(props.customLocation || '') ? (
                    <EventLocationLink
                      href={props.customLocation || undefined}
                      target="_blank"
                    >
                      {props.customLocation}
                    </EventLocationLink>
                  ) : (
                    props.customLocation
                  )}
                </EventLocation>
              )}
              {!props.event?.isParent && (
                <ContactContainer>
                  {props.guestInfo && (
                    <Accordion
                      expanded={state.isOpenContact}
                      onChange={actions.onToggleContact}
                      summary={
                        <EventGuestSummary>
                          <DescriptionTitle>
                            {t('Guest Info')}
                            <ExpandIcon
                              name={
                                state.isOpenDescription
                                  ? 'expand_less'
                                  : 'expand_more'
                              }
                              size={fontSize(20)}
                            />
                          </DescriptionTitle>
                          <EventGuestInfoEditButton
                            onClick={(e: SyntheticEvent<HTMLDivElement>) => {
                              actions.onClickEditContact(e);
                            }}
                            onKeyPress={(
                              e: KeyboardEvent<HTMLInputElement>,
                            ) => {
                              if (e.key === 'Enter') {
                                actions.onClickEditContact(e);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            <EditIcon name={'edit'} />
                            <div>{t('Edit')}</div>
                          </EventGuestInfoEditButton>
                        </EventGuestSummary>
                      }
                      details={
                        <EventGuestInfo>
                          {props.guestInfo.companyName && (
                            <EventGuestInfoItem>
                              <EventGuestInfoItemIcon
                                name="office-building-outline"
                                size={fontSize(18)}
                              />
                              {props.guestInfo.companyName}
                            </EventGuestInfoItem>
                          )}
                          {props.guestInfo.email && (
                            <EventGuestInfoItem>
                              <EventGuestInfoItemIcon
                                name="email-outline"
                                size={fontSize(18)}
                              />
                              {props.guestInfo.email}
                            </EventGuestInfoItem>
                          )}
                          {props.guestInfo.phoneNumber && (
                            <EventGuestInfoItem>
                              <EventGuestInfoItemIcon
                                name="phone"
                                size={fontSize(18)}
                              />
                              {props.guestInfo.phoneNumber}
                            </EventGuestInfoItem>
                          )}
                          {props.guestInfo.textRemindCountryCode &&
                            props.guestInfo.textRemindNumber && (
                              <EventGuestInfoItem>
                                <EventGuestInfoItemIcon
                                  name="message-badge-outline"
                                  size={fontSize(18)}
                                />
                                {props.guestInfo.textRemindCountryCode}
                                {props.guestInfo.textRemindNumber}
                              </EventGuestInfoItem>
                            )}
                          {(props.guestInfo.answers || []).length > 0 && (
                            <EventGuestAnswers>
                              {props.guestInfo.answers?.map((answer) => (
                                <>
                                  <EventGuestAnswerTitle>
                                    {answer.question}
                                  </EventGuestAnswerTitle>
                                  <EventGuestAnswerBody>
                                    {toContentText(answer)}
                                  </EventGuestAnswerBody>
                                </>
                              ))}
                            </EventGuestAnswers>
                          )}
                        </EventGuestInfo>
                      }
                      hiddenExpandIcon
                    />
                  )}
                </ContactContainer>
              )}
              {props?.event?.payment && isShowPrice && (
                <ContactContainer>
                  <Accordion
                    expanded={state.isOpenPayment}
                    onChange={actions.onTogglePayment}
                    summary={
                      <DescriptionTitle>
                        {t('Payment Info')}
                        <ExpandIcon
                          name={
                            state.isOpenDescription
                              ? 'expand_less'
                              : 'expand_more'
                          }
                          size={fontSize(20)}
                        />
                      </DescriptionTitle>
                    }
                    details={
                      <EventGuestInfo>
                        <EventContentPaymentDetail
                          event={props.event}
                          isHost={true}
                        />
                      </EventGuestInfo>
                    }
                    hiddenExpandIcon
                  />
                </ContactContainer>
              )}
              <DescriptionContainer>
                {props.description && (
                  <Accordion
                    expanded={state.isOpenDescription}
                    onChange={actions.onToggleDescription}
                    summary={
                      <DescriptionTitle>
                        {t('Description')}
                        <ExpandIcon
                          name={
                            state.isOpenDescription
                              ? 'expand_less'
                              : 'expand_more'
                          }
                          size={fontSize(20)}
                        />
                      </DescriptionTitle>
                    }
                    details={
                      <EventDescriptionExpand>
                        <StyledRichReadView
                          content={autolink(props.description)}
                        />
                      </EventDescriptionExpand>
                    }
                    hiddenExpandIcon
                  />
                )}
              </DescriptionContainer>
            </EventContentHeader>
            <EventContent canShowDetail={props.canShowDetail}>
              {props.canShowDetail && props.event && props.event.isParent ? (
                <EventContentSection>
                  <Accordion
                    expanded={state.isOpenGuestList}
                    onChange={actions.onToggleGuestList}
                    summary={<SectionTitle>{t('Guest List')}</SectionTitle>}
                    details={
                      <EventContentSectionBody>
                        <GuestListTabs>
                          <Tabs
                            indicatorColor="primary"
                            value={state.activeTab}
                            onChange={actions.onChangeTab}
                            tabs={guestListTabs}
                          />
                        </GuestListTabs>
                        <GuestListContainer>
                          {childEvents.length === 0 ? (
                            <GuestListEmptyText>
                              {state.childEventsIsLoading ? (
                                <>
                                  <GuestContainer>
                                    <Skeleton
                                      variant="rectangular"
                                      width="100%"
                                      height="24"
                                    />
                                  </GuestContainer>
                                  <GuestContainer>
                                    <Skeleton
                                      variant="rectangular"
                                      width="100%"
                                      height="24"
                                    />
                                  </GuestContainer>
                                </>
                              ) : (
                                <>
                                  {state.activeTab === 0
                                    ? t('Going Guest Is Empty')
                                    : t('Cancelled Guest Is Empty')}
                                </>
                              )}
                            </GuestListEmptyText>
                          ) : (
                            <>
                              <GuestListHeaderContainer>
                                <GuestContainerLeft>
                                  <GuestListHeader>
                                    <GuestListHeaderTitle>
                                      {t('Guest Name')}
                                    </GuestListHeaderTitle>
                                  </GuestListHeader>
                                </GuestContainerLeft>
                                {!isPoll && (
                                  <GuestContainerRight>
                                    <GuestListHeader>
                                      <GuestListHeaderTitle>
                                        {t('Event Created')}
                                      </GuestListHeaderTitle>
                                    </GuestListHeader>
                                  </GuestContainerRight>
                                )}
                              </GuestListHeaderContainer>
                              {childEvents.map((event, index) => {
                                const guest = event.users.find(
                                  (u) => u.userType === UserType.Guest,
                                );
                                return (
                                  <GuestContainer>
                                    <GuestContainerLeft>
                                      <GuestProfileContainer>
                                        <AvatarContainer>
                                          <Avatar
                                            src={guest?.profileImage}
                                            alt={event?.contact?.name}
                                          />
                                        </AvatarContainer>
                                        <GuestNameContainer>
                                          <GuestName>
                                            {event?.contact?.name}
                                          </GuestName>
                                          {event.contact?.company && (
                                            <GuestCompany>
                                              {event.contact.company}
                                            </GuestCompany>
                                          )}
                                          {event.contact?.email && (
                                            <GuestEmail>
                                              {event.contact.email}
                                            </GuestEmail>
                                          )}
                                        </GuestNameContainer>
                                      </GuestProfileContainer>
                                      <ShownSPSite>
                                        <EventUpdateDateHeader>
                                          {t('Event Created')}
                                        </EventUpdateDateHeader>
                                        <EventUpdateDate>
                                          {formatToYMD(
                                            event.createdAt,
                                            event.timeZone,
                                            { delimiter: '/' },
                                          )}
                                        </EventUpdateDate>
                                        <EventUpdateTime>
                                          {formatToTime(
                                            event.createdAt,
                                            event.timeZone,
                                          )}
                                        </EventUpdateTime>
                                      </ShownSPSite>
                                    </GuestContainerLeft>
                                    {!isPoll && (
                                      <GuestContainerRight>
                                        <ShownPCSite>
                                          <EventDate>
                                            <EventDateIcon
                                              name="clock-time-nine-outline"
                                              size={fontSize(16)}
                                            />
                                            <EventUpdateDate>
                                              {formatToYMD(
                                                event.createdAt,
                                                event.timeZone,
                                                { delimiter: '/' },
                                              )}
                                            </EventUpdateDate>
                                            <EventUpdateTime>
                                              {formatToTime(
                                                event.createdAt,
                                                event.timeZone,
                                              )}
                                            </EventUpdateTime>
                                          </EventDate>
                                        </ShownPCSite>
                                        <OpenChildEventLink>
                                          <Icon
                                            name="chevron-right"
                                            color="primary"
                                            size={fontSize(16)}
                                          />
                                          <Link
                                            href={dashboardHostEventPath(
                                              event.uid,
                                              { from: router.asPath },
                                            )}
                                          >
                                            <EventDetailText>
                                              {t('Event Detail')}
                                            </EventDetailText>
                                          </Link>
                                        </OpenChildEventLink>
                                      </GuestContainerRight>
                                    )}
                                  </GuestContainer>
                                );
                              })}
                            </>
                          )}
                        </GuestListContainer>
                        {state.numberOfPages > 1 && (
                          <PaginationContainer>
                            <Pagination
                              currentPage={state.currentPage}
                              numberOfPages={state.numberOfPages}
                              onChange={actions.onChangePage}
                            />
                          </PaginationContainer>
                        )}
                      </EventContentSectionBody>
                    }
                  />
                </EventContentSection>
              ) : null}
            </EventContent>
          </>
        }
      />
      <ConfirmationDialog
        isLoading={state.isCancelLoading}
        isOpen={state.cancelEventUid !== null}
        onSubmit={actions.onCancelEvent}
        submitLabel={translate('Yes')}
        onCancel={actions.onCloseDialog}
        cancelLabel={translate('No')}
        title={t('Cancel?')}
        text={cancelDialogText}
      />
      <ConfirmationDialog
        isLoading={state.isCancelLoading}
        isOpen={state.confirmDelete}
        onSubmit={actions.onClickDeleteEvent}
        submitLabel={translate('Yes')}
        onCancel={actions.onCloseDialog}
        cancelLabel={translate('No')}
        title={t('Delete?')}
      />
      {state.editEventUid && (
        <ContactEditForm
          viewer={props.viewer}
          isOpen={state.editEventUid !== null}
          onUpdated={onUpdateAnswer}
          onCancel={actions.onCloseDialog}
          event={props.event ?? null}
        />
      )}
      {state.snackbarMessage && (
        <Snackbar
          onClose={actions.onCloseSnackbar}
          isOpen={true}
          severity={SeverityType.Success}
          message={state.snackbarMessage}
        />
      )}
      {state.snackbarErrorMessage && (
        <Snackbar
          onClose={actions.onCloseSnackbar}
          isOpen={true}
          severity={SeverityType.Error}
          message={state.snackbarErrorMessage}
        />
      )}
    </>
  );
}

export const LoadingComponent = () => {
  const t = translateInComponent(translations);

  const [isElectron, setIsElectron] = useState<boolean>(false);

  useEffect(() => {
    setIsElectron(isInElectron());
  }, []);

  const router = useRouter();
  const onClickBack = () => {
    moveFromOrDefaultPath(router, dashboardPath());
  };

  return (
    <LoadingComponentTemplate
      header={
        <>
          <HeaderTitle isElectron={isElectron}>
            <Skeleton width="100px" />
          </HeaderTitle>
          <HeaderButtonContainer>
            <Tooltip title={t('Close')}>
              <CloseButtonWrap onClick={onClickBack}>
                <CloseIcon name="close" />
              </CloseButtonWrap>
            </Tooltip>
          </HeaderButtonContainer>
        </>
      }
      mainContent={
        <>
          <EventContentHeader>
            <EventTitle>
              <Skeleton width="200px" />
            </EventTitle>
            <EventDateContainer>
              <EventDate>
                <EventDateIcon
                  name="clock-time-nine-outline"
                  size={fontSize(18)}
                />
                <Skeleton width="200px" />
              </EventDate>
            </EventDateContainer>
            <EventSummary>
              <StyledIcon name="account" size={fontSize(18)} />
              <Skeleton width="100px" />
            </EventSummary>
            <EventLocation>
              <EventLocationIcon name="map-marker" size={fontSize(18)} />
              <Skeleton width="100px" />
            </EventLocation>
            <Accordion
              expanded={false}
              summary={
                <DescriptionTitle>
                  {t('Description')}
                  <ExpandIcon name="expand_more" size={fontSize(20)} />
                </DescriptionTitle>
              }
              details={null}
              hiddenExpandIcon
            />
          </EventContentHeader>

          <EventContent>
            <EventContentSection>
              <EventContentSection>
                <Accordion
                  expanded
                  summary={
                    <SectionTitle>
                      <Skeleton width="100px" />
                    </SectionTitle>
                  }
                  details={
                    <EventContentSectionBody>
                      <Skeleton />
                    </EventContentSectionBody>
                  }
                />
              </EventContentSection>
            </EventContentSection>
          </EventContent>
        </>
      }
    />
  );
};

const HeaderTitle = styled.div<{ isElectron?: boolean }>`
  display: flex;
  align-items: center;
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};

  ${(props) =>
    props.isElectron &&
    css`
      margin-left: ${spacing(80)};
      -webkit-app-region: drag;
    `}
`;
const HeaderTitleText = styled.div`
  margin-right: ${spacing(8)};
`;
const HeaderButtonContainer = styled.div`
  display: flex;
`;
const SettingButtonWrap = styled(IconButton)`
  padding: ${spacing(4)};
  margin-left: ${spacing(8)};
`;
const SettingIcon = styled(Icon)`
  color: ${gray01};
`;
const CloseButtonWrap = styled(IconButton)`
  padding: ${spacing(4)};
  margin-left: ${spacing(4)};
`;
const CloseIcon = styled(Icon)`
  color: ${gray01};
`;

const EventContentHeader = styled.div`
  padding: ${spacing(24)};
  border-bottom: 1px solid ${gray04};
  background-color: ${paper};

  ${mediaMax.tablet} {
    padding: ${spacing(12)};
  }
`;
const EventTitle = styled.h1`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(4)};
  font-size: ${fontSize(20)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(18)};
  }
`;
const EventNameIcon = styled(Image)`
  margin-right: ${spacing(4)};

  ${mediaMax.tablet} {
    width: 16px;
    height: 16px;
  }
`;
const UserContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(8)};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(4)};
  }
`;
const UserName = styled.span`
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
  margin-right: ${spacing(16)};

  ${mediaMax.tablet} {
    margin-right: ${spacing(8)};
  }
`;
const EventDateIcon = styled(Icon)`
  flex-shrink: 0;
  margin-right: ${spacing(4)};
`;
const EventDateContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(4)};
  font-size: ${fontSize(13)};
`;
const EventDateBox = styled.div`
  display: flex;
  align-items: center;

  ${mediaMax.desktop} {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const EventDate = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;
const GuestEventDate = styled.div`
  display: flex;
  align-items: center;
  margin-left: ${spacing(8)};
  color: ${gray01};
  font-size: ${fontSize(12)};
  word-break: break-all;

  ${mediaMax.desktop} {
    margin-left: 0;
    margin-top: ${spacing(4)};
  }
`;
const GuestEventDateTitle = styled.div`
  flex-shrink: 0;
  background-color: ${gray05};
  border-radius: ${borderRadius(4)};
  font-size: ${fontSize(11)};
  padding: ${spacing(4)};
  margin-right: ${spacing(4)};
`;
const EventRoutineSchedule = styled.div`
  padding-left: ${spacing(16)};
  border-left: 1px solid ${gray04};
  margin-left: ${spacing(16)};

  ${mediaMax.tablet} {
    padding-left: ${spacing(8)};
    margin-left: ${spacing(8)};
  }
`;
const EventSummary = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-bottom: ${spacing(8)};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(4)};
  }
`;
const StyledSummaryContent = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(8)};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(4)};
  }
`;
const StyledIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  color: ${gray02};
`;
const StyledIconContent = styled.div`
  margin-right: ${spacing(12)};

  ${mediaMax.tablet} {
    margin-right: ${spacing(8)};
  }
`;
const DescriptionTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  color: ${gray02};
  margin-bottom: ${spacing(4)};
`;
const EventDescriptionExpand = styled.div`
  font-size: ${fontSize(13)};
  white-space: pre;
  overflow: auto;
`;
const EventCapacity = styled.div`
  display: flex;
  align-items: center;
  color: ${gray01};
  margin-bottom: ${spacing(8)};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(4)};
  }
`;
const EventCapacityDetail = styled.div`
  margin: 0px ${spacing(4)};
  font-size: ${fontSize(12)};
  line-height: 18px;
`;
const EventGuestSummary = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;
const EventGuestInfoEditButton = styled.div`
  display: flex;
  color: ${gray01};
  font-size: ${fontSize(14)};
`;
const EditIcon = styled(Icon)`
  font-size: ${fontSize(20)} !important;
  width: 20px !important;
  height: 20px !important;
  color: ${gray01};
  margin-right: ${spacing(3)};
`;
const EventGuestInfo = styled.div`
  padding: ${spacing(12)};
  background-color: ${blueGrayPale};
  border-radius: ${borderRadius(12)};
  font-size: ${fontSize(13)};
  color: ${gray01};
`;
const EventGuestInfoItemIcon = styled(Icon)`
  flex-shrink: 0;
  margin-right: ${spacing(4)};
`;
const EventGuestInfoItem = styled.div`
  display: inline-flex;
  align-items: center;
  margin-right: ${spacing(16)};
  word-break: break-word;
`;
const EventGuestAnswers = styled.div`
  padding-top: ${spacing(12)};
  margin-top: ${spacing(12)};
  border-top: 1px solid ${gray04};
`;
const EventGuestAnswerTitle = styled.div`
  margin-bottom: ${spacing(4)};
  color: ${black};
  font-weight: ${fontWeight('bold')};
`;
const EventGuestAnswerBody = styled.div`
  margin-bottom: ${spacing(8)};
  white-space: pre-wrap;
  word-break: break-word;

  &:last-of-type {
    margin-bottom: 0;
  }
`;
const ExpandIcon = styled(Icon)`
  min-width: 20px;
  min-height: 20px;
`;
const EventLocation = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(4)};
  font-size: ${fontSize(13)};
`;
const EventLocationIcon = styled(Icon)`
  flex-shrink: 0;
  margin-right: ${spacing(4)};
  color: ${gray02};
`;
const EventLocationLink = styled.a`
  text-overflow: ellipsis;
  overflow: hidden;
`;
const LocationLink = styled(Link)`
  display: block;
  color: ${black};
  margin-right: ${spacing(12)};
  display: flex;
  align-items: center;
`;
const CreatePageButton = styled.div`
  cursor: pointer;
  display: block;
  color: ${primary};
  margin-right: ${spacing(12)};
  display: flex;
  align-items: center;
`;
const LocationLinkIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  color: ${primary};

  ${mediaMax.tablet} {
    margin-right: ${spacing(6)};
  }
`;

const EventContent = styled.div<{ canShowDetail?: boolean }>`
  padding: ${spacing(24)};

  ${mediaMax.tablet} {
    padding: ${spacing(12)};

    ${(props) =>
      !props.canShowDetail &&
      css`
        height: 100vh;
      `}
  }
`;
const EventContentSection = styled.div`
  padding: ${spacing(24)};
  margin-bottom: ${spacing(24)};
  background-color: ${white};
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};

  ${mediaMax.tablet} {
    padding: ${spacing(12)};
    margin-bottom: ${spacing(12)};
  }
`;
const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
`;
const EventContentSectionBody = styled.div`
  margin-top: ${spacing(16)};
  border-top: 1px solid ${gray04};
  padding-top: ${spacing(16)};
  background-color: ${white};

  ${mediaMax.tablet} {
    margin-top: ${spacing(12)};
    padding-top: ${spacing(12)};
  }
`;
const ContactContainer = styled.div`
  margin-bottom: ${spacing(12)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(8)};
  }
`;
const DescriptionContainer = styled.div``;

const GuestListTabs = styled.div`
  width: 100%;
  border-bottom: 1px solid ${gray04};
  ${mediaMax.desktop} {
    /* padding: 0 ${spacing(24)}; */
  }
  ${mediaMax.xs} {
    width: 100%;
  }
`;
const GuestListTabContainer = styled.div`
  display: flex;
`;
const GuestListTabText = styled.div`
  line-height: 21px;
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(14)};
  margin-right: ${spacing(8)};
  text-align: center;
`;
const GuestListTabCount = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: ${gray06};
  border-radius: 60px;
`;
const GuestListTabCountInnter = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(11)};
  line-height: 15px;
  text-align: center;
  letter-spacing: 0.05em;
  color: ${gray01};
  margin: 0 auto;
`;
const GuestListContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const GuestContainer = styled.div`
  display: flex;
  padding: ${spacing(12)} 0px;
  border-bottom: 1px solid ${gray04};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    margin-bottom: ${spacing(12)};
  }
`;
const GuestContainerLeft = styled.div`
  display: flex;
  align-items: center;
  flex: 6;

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    flex-direction: column;
    justify-content: space-between;
    align-items: start;
  }
`;
const GuestContainerRight = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 4;

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    justify-content: flex-end;
  }
`;

const GuestListHeaderContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${gray04};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    display: none;
    margin-bottom: ${spacing(12)};
  }
`;

const GuestListHeader = styled.div`
  margin-right: ${spacing(16)};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    margin-right: 0;
    margin-bottom: ${spacing(16)};
    &:last-of-type {
      margin-bottom: 0;
    }
  }
`;
const GuestListHeaderTitle = styled.div`
  padding-top: ${spacing(16)};
  padding-bottom: ${spacing(4)};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(13)};
  line-height: 19px;
  color: ${gray02};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    margin-bottom: ${spacing(8)};
  }
`;
const GuestProfileContainer = styled.div`
  display: flex;
`;
const AvatarContainer = styled.div`
  margin-right: ${spacing(16)};
`;
const GuestName = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(14)};
  line-height: 21px;
  color: ${black};
`;
const GuestCompany = styled.div`
  font-size: ${fontSize(12)};
  line-height: 18px;
  color: ${gray01};
`;
const GuestEmail = styled.div`
  font-size: ${fontSize(12)};
  line-height: 18px;
  color: ${gray01};
`;
const EventUpdateDateHeader = styled.div`
  display: none;
  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    display: block;
    font-size: ${fontSize(12)};
    line-height: 27px;
    letter-spacing: 0.05em;
    color: ${gray01};
    margin-top: 8px;
  }
`;
const EventUpdateDate = styled.span`
  font-size: ${fontSize(13)};
  line-height: 19px;
  letter-spacing: 0.05em;
  color: ${gray01};
  margin-right: ${spacing(3)};
`;
const EventUpdateTime = styled.span`
  font-size: ${fontSize(13)};
  line-height: 19px;
  letter-spacing: 0.05em;
  color: ${gray01};
`;
const EventDetailText = styled.a`
  color: ${black};
  :hover {
    color: ${primary};
  }
`;
const OpenChildEventLink = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${spacing(8)};
`;
const ShownPCSite = styled.div`
  ${mediaMax.tablet} {
    display: none;
  }
`;
const ShownSPSite = styled.div`
  display: none;
  ${mediaMax.tablet} {
    display: block;
  }
`;
const PaginationContainer = styled.div`
  padding-top: ${spacing(24)};
  display: flex;
  justify-content: center;
`;
const GuestListEmptyText = styled.div`
  font-size: ${fontSize(14)};
  margin-top: ${spacing(8)};
`;
const GuestNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MetaTagList = styled.div`
  margin-bottom: ${spacing(8)};
`;

const CompanyName = styled.span`
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  color: ${gray02};
`;
const EventTypeTitle = styled.h1`
  font-size: ${fontSize(13)};
`;

const CalendarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Calendar = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(13)};
`;
const CalendarName = styled.div``;

const CalendarLink = styled(Link)`
  color: ${black};
`;

const ActivityList = styled.div`
  margin-top: ${spacing(32)};
  ${mediaMax.tablet} {
    margin: ${spacing(16)} ${spacing(12)};
  }
`;
const VideoList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin: ${spacing(12)} 0 ${spacing(8)};
  ${mediaMax.tablet} {
    display: block;
  }
`;
const StyledMeetingButton = styled(MeetingButton)`
  width: 100%;
  margin-bottom: ${spacing(12)};
  ${mediaMax.tablet} {
  }
`;
const StyledRichReadView = styled(RichReadView)`
  font-size: ${fontSize(15)};
`;
const StyledConfirmRequestButtonWrapper = styled.div`
  margin-bottom: ${spacing(24)};
`;
